import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * PrimeNG Aura preset aligned to the Stelmach brand palette.
 *
 * Colors mirror the Tailwind tokens in `src/styles/tokens.css` so
 * PrimeNG components and Tailwind utilities share one source of truth.
 * `palette()` anchors each base color at shade 500 and generates the 50–950
 * ramp around it. The app runs light-mode only (`darkModeSelector: false`),
 * so only the light colorScheme is overridden.
 */
export const StelmachPreset = definePreset(Aura, {
    primitive: {
        // Severity palettes — Aura maps button/message/tag severities to these:
        // danger→red, success→green, info→sky, warn→orange.
        red: palette('#da0d0d'), // danger + invalid form state
        green: palette('#288a51'), // success
        sky: palette('#00a6e6'), // info
        orange: palette('#f6cf23'), // warn (brand warning yellow)
    },
    semantic: {
        // Brand green primary ramp (primary.500 = #257948); feeds primary.color,
        // hover/active, focus ring, highlight backgrounds, etc.
        primary: palette('#257948'),
        colorScheme: {
            light: {
                // Neutral grayscale — matches the project's non-cool grays and
                // replaces Aura's blue-tinted slate. Surface 0 stays pure white.
                surface: {
                    0: '#ffffff',
                    50: '{neutral.50}',
                    100: '{neutral.100}',
                    200: '{neutral.200}',
                    300: '{neutral.300}',
                    400: '{neutral.400}',
                    500: '{neutral.500}',
                    600: '{neutral.600}',
                    700: '{neutral.700}',
                    800: '{neutral.800}',
                    900: '{neutral.900}',
                    950: '{neutral.950}',
                },
            },
        },
    },
    components: {
        // Tag — solid brand-colored backgrounds with white text. Aura's default
        // tag styling is tonal (pale background + colored text); these overrides
        // mirror the former custom `.badge-pill` look so order/lead status & kind
        // tags keep their solid appearance from one themed source. Light mode only.
        tag: {
            colorScheme: {
                light: {
                    success: { background: '{green.500}', color: '#ffffff' },
                    info: { background: '{sky.500}', color: '#ffffff' },
                    danger: { background: '{red.500}', color: '#ffffff' },
                    warn: { background: '#ffa113', color: '#ffffff' }, // brand secondary/amber — no primitive equivalent
                },
            },
        },
        // Tooltip — lighter than Aura's default: tighter padding and a larger
        // gutter (gap from the target). Font size has no token, so it's set on
        // `.p-tooltip-text` in src/styles/app.css. Aura defaults were
        // padding 0.5rem 0.75rem / gutter 0.25rem.
        tooltip: {
            root: {
                padding: '0.25rem 0.5rem',
                gutter: '0.5rem',
            },
        },
    },
});
